import clsx from "clsx";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import {
  Button,
  LoadIndicator,
  LoadPanel,
  Lookup,
  ScrollView,
  SelectBox,
} from "devextreme-react";
import Calendar, { CalendarRef } from "devextreme-react/calendar";
import { Item, Toolbar } from "devextreme-react/toolbar";
import notify from "../../lib/notify";
import i18n from "i18next";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadAbsence } from "../../api/calendarData";
import WorkPlanEditor from "../../components/work-plan-editor/WorkPlanEditor";
import { useAuth } from "../../contexts/auth";
import axios from "../../lib/axios";
import {
  EmploymentSpan,
  getEmploymentPeriods,
  isDayWithinEmployment,
} from "../../lib/employment";
import { ManagerSubordinate, WorkPlan } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import WorkPlanForEmployee from "./WorkPlanForEmployee";

import "../calendar/Calendar.scss";

const currentDate = new Date();

export default function WorkPlanForManager() {
  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const [subordinates, setSubordinates] = useState<ManagerSubordinate[]>([]);
  const [subordinatesLoading, setSubordinatesLoading] = useState(false);
  const [selectedSubordinateId, setSelectedSubordinateId] = useState<string>();
  const [viewMode, setViewMode] = useState<"employee" | "employees">(
    "employees",
  );
  const [selectedEmployee, setSelectedEmployee] = useState<{
    companyId: number;
    employeeId: number;
    name: string;
  }>();

  const calendarRef = useRef<CalendarRef>(null);
  const [startDate, setStartDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
  );
  const [days, setDays] = useState<WorkPlan[]>([]);
  const [workPlanLoading, setWorkPlanLoading] = useState(false);
  const [showWorkPlanEditor, setShowWorkPlanEditor] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const { user } = useAuth();

  const [holidayData, setHolidayData] = useState<any[]>([]);
  // Przedziały zatrudnienia wybranego pracownika (undefined => brak danych, nie blokujemy).
  const [employmentSpans, setEmploymentSpans] = useState<EmploymentSpan[]>();

  // Index work plan days by date string for O(1) lookup in cellRender
  const daysMap = useMemo(() => {
    const map = new Map<string, WorkPlan>();
    for (const d of days) {
      map.set(d.date, d);
    }
    return map;
  }, [days]);

  // Index selected dates by dateString for O(1) lookup
  const selectedDatesSet = useMemo(() => {
    const set = new Set<string>();
    for (const d of selectedDates) {
      set.add(d.toDateString());
    }
    return set;
  }, [selectedDates]);

  const getSubordinates = useCallback(() => {
    setSubordinatesLoading(true);

    axios
      .get<ManagerSubordinate[]>("/api/manager/subordinates", {
        params: {
          companyId: user?.companyId || 0,
          employeeId: user?.employeeId || 0,
          from: format(startOfMonth(startDate), "yyyy-MM-dd"),
          to: format(endOfMonth(startDate), "yyyy-MM-dd"),
        },
        headers: {
          "Accept-Language": i18n.language,
        },
      })
      .then((response) => {
        setSubordinates(response.data);
        if (response.data.length > 0) {
          setSelectedSubordinateId(
            `${response.data[0].companyId},${response.data[0].employeeId}`,
          );
          setWorkPlanLoading(true);
        }
      })
      .catch(() => {
        notify(t("errorOccurredWhileDataDownload"), "error");
      })
      .finally(() => {
        setSubordinatesLoading(false);
      });
  }, [t, startDate]);

  const getWorkPlanForEmployee = useCallback(
    (employeeId: number, companyId: number) => {
      setWorkPlanLoading(true);

      axios
        .get<WorkPlan[]>(`/api/work-plan-for-employee`, {
          params: {
            employeeId: employeeId,
            companyId: companyId,
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
          },
          headers: {
            "Accept-Language": i18n.language,
          },
        })
        .then((response) => {
          setDays(response.data);
        })
        .catch(() => {
          notify(t("errorOccurredWhileDataDownload"), "error");
        })
        .finally(() => {
          setWorkPlanLoading(false);
        });
    },
    [startDate, t],
  );

  useEffect(() => {
    getSubordinates();
  }, [getSubordinates]);

  useEffect(() => {
    if (selectedSubordinateId) {
      const [, employeeId] = selectedSubordinateId.split(",");
      const [companyId] = selectedSubordinateId.split(",");
      getWorkPlanForEmployee(Number(employeeId), Number(companyId));
    }
  }, [getWorkPlanForEmployee, selectedSubordinateId]);

  useEffect(() => {
    const splitedId = selectedSubordinateId?.split(",");
    if (splitedId) {
      const employee = subordinates.find(
        (s) =>
          s.companyId === Number(splitedId[0]) &&
          s.employeeId === Number(splitedId[1]),
      );
      if (employee) {
        setSelectedEmployee({
          companyId: employee.companyId,
          employeeId: employee.employeeId,
          name: `${employee.lastName} ${employee.firstName}`,
        });
      }
    }
  }, [selectedSubordinateId, subordinates]);

  useEffect(() => {
    if (
      !selectedEmployee ||
      typeof selectedEmployee.employeeId !== "number" ||
      typeof selectedEmployee.companyId !== "number"
    ) {
      return;
    }

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const employeeId = selectedEmployee.employeeId;
    const empCompanyId = selectedEmployee.companyId;

    downloadAbsence(year, month, [employeeId], [empCompanyId])
      .then((res) => {
        if (res.isOk && Array.isArray(res.data)) {
          const flattened = res.data.flatMap((emp: any) =>
            (emp.absences || []).map((a: any) => ({
              employeeId: emp.employeeId,
              fromDate: a.fromDate,
              toDate: a.toDate,
              name: a.name,
              type: a.type,
            })),
          );
          setHolidayData(flattened);
        } else {
          setHolidayData([]);
        }
      })
      .catch(() => notify(t("errorOccurredWhileDataDownload"), "error"));
  }, [selectedEmployee]);

  // Przedziały zatrudnienia wybranego pracownika dla widocznego miesiąca - do blokady dni poza umową.
  useEffect(() => {
    if (
      !selectedEmployee ||
      typeof selectedEmployee.employeeId !== "number" ||
      typeof selectedEmployee.companyId !== "number"
    ) {
      setEmploymentSpans(undefined);
      return;
    }

    const from = format(startOfMonth(startDate), "yyyy-MM-dd");
    const to = format(endOfMonth(startDate), "yyyy-MM-dd");

    getEmploymentPeriods(
      [
        {
          companyId: selectedEmployee.companyId,
          employeeId: selectedEmployee.employeeId,
        },
      ],
      from,
      to,
    )
      .then((periods) => setEmploymentSpans(periods[0]?.spans ?? []))
      .catch(() => setEmploymentSpans(undefined)); // fail-open
  }, [selectedEmployee, startDate]);

  const handleCellClick = useCallback(
    (event: React.MouseEvent, clickedDate: Date) => {
      event.preventDefault();
      event.stopPropagation();

      const effectiveEmployee = selectedEmployee || {
        employeeId: user?.employeeId,
        companyId: user?.companyId,
      };

      if (!effectiveEmployee?.employeeId || !effectiveEmployee?.companyId)
        return;

      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );

      // Liczba dni roboczych (pon-pt) od początku bieżącego miesiąca do dziś.
      let workingDayCount = 0;
      for (let d = 1; d <= today.getDate(); d++) {
        const dayOfWeek = new Date(
          today.getFullYear(),
          today.getMonth(),
          d,
        ).getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workingDayCount++;
        }
      }

      // Edycja poprzedniego miesiąca dozwolona do 4. dnia roboczego włącznie;
      // od 5. dnia roboczego blokada już obowiązuje.
      const earliestAllowedMonthStart =
        workingDayCount < 5
          ? subMonths(currentMonthStart, 1)
          : currentMonthStart;
      const clickedMonthStart = new Date(
        clickedDate.getFullYear(),
        clickedDate.getMonth(),
        1,
      );

      if (clickedMonthStart < earliestAllowedMonthStart) {
        notify(t("workPlan.editPastMonthBlocked"), "warning");
        return;
      }

      const isAbsence = holidayData.some(
        (a) =>
          a.employeeId === effectiveEmployee.employeeId &&
          new Date(a.fromDate) <= clickedDate &&
          new Date(a.toDate) >= clickedDate,
      );

      if (isAbsence) {
        notify(t("workPlan.popupBlock"), "warning");
        return;
      }

      // blokada dni poza okresem zatrudnienia pracownika (umowa do X / od Y)
      if (!isDayWithinEmployment(employmentSpans, clickedDate)) {
        notify(t("workPlan.outsideEmployment"), "warning");
        return;
      }

      setSelectedDates((prev) => {
        const already = prev.some(
          (d) => d.toDateString() === clickedDate.toDateString(),
        );
        return already
          ? prev.filter((d) => d.toDateString() !== clickedDate.toDateString())
          : [...prev, clickedDate];
      });
    },
    [selectedEmployee, user, holidayData, employmentSpans, t],
  );

  function preventMouseDown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  useEffect(() => {
    if (selectedDates.length !== 0) {
      document.body.addEventListener("mousedown", preventMouseDown, {
        passive: false,
      });
    } else {
      document.body.removeEventListener("mousedown", preventMouseDown);
    }

    return () => {
      document.body.removeEventListener("mousedown", preventMouseDown);
    };
  }, [selectedDates]);

  const cellRender = useCallback(
    ({ date, text, view }: { date: Date; text: string; view: string }) => {
      if (view !== "month") return <span>{text}</span>;

      const employeeId = selectedEmployee?.employeeId;
      if (!employeeId) return <span>{text}</span>;

      const absence = holidayData.find(
        (a) =>
          a.employeeId === employeeId &&
          new Date(a.fromDate) <= date &&
          new Date(a.toDate) >= date,
      );

      const isHoliday = !!absence;
      let holidayColor = "";

      if (isHoliday) {
        if (
          absence?.type === 4 ||
          absence?.name?.toLowerCase().includes("urlop")
        ) {
          holidayColor = "#86efac";
        } else if (absence?.name?.toLowerCase().includes("opieka")) {
          holidayColor = "#facc15";
        } else {
          holidayColor = "#f87171";
        }
      }

      const day = daysMap.get(format(date, "yyyy-MM-dd'T'00:00:00"));
      const isSelected = selectedDatesSet.has(date.toDateString());
      const outsideContract = !isDayWithinEmployment(employmentSpans, date);

      return (
        <div
          className={clsx(
            "min-h-[100px] p-3 flex flex-col justify-center items-center h-full rounded-lg transition-colors",
            isHoliday
              ? "opacity-90 text-black"
              : day?.dayType === 2
                ? "bg-[#4d7c0f] text-white"
                : day?.dayType === 3
                  ? "bg-[#a3e635] text-black"
                  : "bg-[#e9e9e9] text-black dark:bg-[#383838] dark:text-[#eaeaea]",
            isSelected && "selected-day",
            outsideContract && "opacity-40",
          )}
          style={{
            backgroundColor: isHoliday ? holidayColor : undefined,
            cursor: isHoliday || outsideContract ? "not-allowed" : "pointer",
          }}
          title={outsideContract ? t("workPlan.outsideEmployment") : undefined}
          onClick={(event) => handleCellClick(event, date)}
        >
          <div className="mb-1 text-base font-bold">{text}</div>

          {day ? (
            <>
              <div className="text-xs">{day.firstLine}</div>
              <div className="text-xs">{day.secondLine}</div>
            </>
          ) : (
            <div className="text-xs text-gray-500">&ndash;</div>
          )}
        </div>
      );
    },
    [
      daysMap,
      selectedDatesSet,
      selectedEmployee,
      holidayData,
      employmentSpans,
      handleCellClick,
      t,
    ],
  );

  const disabledDates = useCallback(
    ({ date, view }: { date: Date; view: string }) => {
      if (view === "month") {
        return (
          (date as Date).getMonth() !== startDate.getMonth() ||
          !isDayWithinEmployment(employmentSpans, date)
        );
      }
      return false;
    },
    [startDate, employmentSpans],
  );

  const handleOptionChanged = useCallback((event: any) => {
    if (event.fullName === "currentDate") {
      const currentValue = event.value as Date;
      const previousValue = event.previousValue as Date;

      if (currentValue.getMonth() !== previousValue.getMonth()) {
        setStartDate(
          new Date(currentValue.getFullYear(), currentValue.getMonth(), 1),
        );
      }

      calendarRef.current?.instance().clear();
    }
  }, []);

  const changeMonth = useCallback((direction: "next" | "previous") => {
    setStartDate((prev) =>
      startOfMonth(
        direction === "next" ? addMonths(prev, 1) : subMonths(prev, 1),
      ),
    );
    setSelectedDates([]);
  }, []);

  const goToToday = useCallback(() => {
    setStartDate(startOfMonth(new Date()));
    setSelectedDates([]);
  }, []);

  const localizeFirstLine = useCallback(
    (day: WorkPlan | undefined): string => {
      if (!day) return "";
      if (day.dayType === 2) return t("Day off");
      if (day.dayType === 3) return t("Holiday");
      return day.firstLine;
    },
    [t],
  );

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-6 flex flex-col md:flex-row gap-2 justify-between items-start">
          <div>
            <div className="mb-2 text-2xl font-bold leading-none">
              {t("workPlan.header")}
            </div>
            <div className="text-base">{t("workPlan.headerText")}</div>
          </div>
          <SelectBox
            items={[
              { value: "employee", text: t("Employee") },
              { value: "employees", text: t("Employees") },
            ]}
            valueExpr="value"
            displayExpr="text"
            value={viewMode}
            onValueChanged={(e) => setViewMode(e.value)}
            width={150}
          />
        </div>
        {viewMode === "employee" ? (
          <div className="max-w-5xl mx-auto">
            <WorkPlanForEmployee showHeader={false} />
          </div>
        ) : (
          <>
            {subordinatesLoading || (workPlanLoading && days.length === 0) ? (
              <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />
            ) : null}

            {showWorkPlanEditor && (
              <WorkPlanEditor
                employeesToSelect={subordinates.map(
                  ({ companyId, employeeId, lastName, firstName }) => ({
                    companyId,
                    employeeId,
                    name: `${lastName} ${firstName}`,
                  }),
                )}
                employeesWithDates={[
                  {
                    employee: {
                      companyId: selectedEmployee!.companyId,
                      employeeId: selectedEmployee!.employeeId,
                      name: selectedEmployee!.name,
                    },
                    dates: selectedDates,
                  },
                ]}
                onUpdate={() => {
                  if (selectedEmployee) {
                    getWorkPlanForEmployee(
                      selectedEmployee.employeeId,
                      selectedEmployee.companyId,
                    );
                  }
                  setShowWorkPlanEditor(false);
                  setSelectedDates([]);
                  notify(t("workPlan.updated"), "success");
                }}
                onClose={() => setShowWorkPlanEditor(false)}
                onDelete={async ({ companyId, employeeId, date }) => {
                  await axios.delete("/api/work-plan", {
                    params: {
                      companyId,
                      employeeId,
                      date: format(date, "yyyy-MM-dd"),
                    },
                    headers: {
                      "Accept-Language": i18n.language,
                    },
                  });
                  if (selectedEmployee) {
                    getWorkPlanForEmployee(
                      selectedEmployee.employeeId,
                      selectedEmployee.companyId,
                    );
                  }
                  setShowWorkPlanEditor(false);
                  setSelectedDates([]);
                  notify(t("workPlanEditor.deleted"), "success");
                }}
              />
            )}

            {subordinates.length === 0 ? (
              <div
                className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
                role="alert"
              >
                {t("noSubordinateEmployees")}
              </div>
            ) : isMobile ? (
              <>
                <div className="flex flex-col gap-2 mb-3">
                  <Lookup
                    dataSource={[...subordinates].sort((a, b) =>
                      a.lastName.localeCompare(b.lastName, "pl", {
                        sensitivity: "base",
                      }),
                    )}
                    value={selectedSubordinateId}
                    valueExpr={(item) =>
                      item ? `${item.companyId},${item.employeeId}` : ""
                    }
                    displayExpr={(item: ManagerSubordinate) =>
                      item ? `${item.lastName} ${item.firstName}` : ""
                    }
                    width="100%"
                    onValueChanged={(event) =>
                      setSelectedSubordinateId(event.value)
                    }
                    labelMode="static"
                    label={t("workPlan.employee")}
                    dropDownOptions={{
                      showTitle: false,
                    }}
                  />
                  <Button
                    text={t("workPlan.edit")}
                    width="100%"
                    type="default"
                    onClick={() => setShowWorkPlanEditor(true)}
                    disabled={!selectedDates || selectedDates.length === 0}
                  />
                </div>
                <div className="sticky top-0 z-10 flex items-center gap-1 py-2 bg-white dark:bg-[#1e1e1e]">
                  <Button
                    icon="chevronprev"
                    stylingMode="text"
                    onClick={() => changeMonth("previous")}
                  />
                  <div className="flex-1 text-center text-base font-semibold">
                    {`${_.upperFirst(
                      format(startDate, "LLLL", {
                        locale: i18n.language === "en" ? enUS : pl,
                      }),
                    )} ${format(startDate, "yyyy")}`}
                  </div>
                  <Button
                    icon="chevronnext"
                    stylingMode="text"
                    onClick={() => changeMonth("next")}
                  />
                  <Button
                    text={i18n.language === "en" ? "Today" : "Dziś"}
                    stylingMode="outlined"
                    onClick={goToToday}
                  />
                </div>
                <div className="relative">
                  {workPlanLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/60 z-10 rounded">
                      <LoadIndicator height={40} width={40} />
                    </div>
                  )}
                  <div className="flex flex-col gap-2 pb-4">
                    {eachDayOfInterval({
                      start: startDate,
                      end: endOfMonth(startDate),
                    }).map((d) => {
                      const key = format(d, "yyyy-MM-dd'T'00:00:00");
                      const day = daysMap.get(key);
                      const absence = holidayData.find(
                        (a) =>
                          a.employeeId === selectedEmployee?.employeeId &&
                          new Date(a.fromDate) <= d &&
                          new Date(a.toDate) >= d,
                      );
                      const isHoliday = !!absence;
                      let holidayColor: string | undefined;
                      if (isHoliday) {
                        if (
                          absence?.type === 4 ||
                          absence?.name?.toLowerCase().includes("urlop")
                        ) {
                          holidayColor = "#86efac";
                        } else if (
                          absence?.name?.toLowerCase().includes("opieka")
                        ) {
                          holidayColor = "#facc15";
                        } else {
                          holidayColor = "#f87171";
                        }
                      }
                      const isSelected = selectedDatesSet.has(d.toDateString());
                      const dayShort = _.upperFirst(
                        format(d, "EEEEEE", {
                          locale: i18n.language === "en" ? enUS : pl,
                        }),
                      );
                      return (
                        <div
                          key={key}
                          className={clsx(
                            "flex items-stretch gap-3 p-3 rounded-lg",
                            isHoliday
                              ? "text-black"
                              : day?.dayType === 2
                                ? "bg-[#4d7c0f] text-white"
                                : day?.dayType === 3
                                  ? "bg-[#a3e635] text-black"
                                  : "bg-[#e9e9e9] text-black dark:bg-[#383838] dark:text-[#eaeaea]",
                            isSelected
                              ? "border-[3px] border-[#0ea5e9] shadow-[0_0_0_2px_rgba(14,165,233,0.45)]"
                              : "border-2 border-transparent",
                            isHoliday ? "cursor-not-allowed" : "cursor-pointer",
                          )}
                          style={
                            isHoliday
                              ? { backgroundColor: holidayColor }
                              : undefined
                          }
                          onClick={(event) => {
                            if (isHoliday) return;
                            handleCellClick(event, d);
                          }}
                        >
                          <div className="flex flex-col items-center justify-center min-w-[48px] font-bold">
                            <div className="text-2xl leading-none">
                              {d.getDate()}
                            </div>
                            <div className="text-[11px] uppercase opacity-80 mt-1">
                              {dayShort}
                            </div>
                          </div>
                          <div className="flex-1 flex flex-col justify-center text-sm min-w-0">
                            {isHoliday ? (
                              <div className="font-medium break-words">
                                {absence?.name}
                              </div>
                            ) : day ? (
                              <>
                                <div className="break-words">
                                  {localizeFirstLine(day)}
                                </div>
                                {day.secondLine ? (
                                  <div className="text-xs opacity-85 break-words">
                                    {day.secondLine}
                                  </div>
                                ) : null}
                              </>
                            ) : null}
                          </div>
                          {isSelected ? (
                            <div className="flex items-center text-2xl">
                              <i className="dx-icon-check text-[#0ea5e9]"></i>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <>
                <Toolbar className="mx-auto max-w-5xl mb-6">
                  <Item location="before" locateInMenu="auto">
                    <Lookup
                      dataSource={[...subordinates].sort((a, b) =>
                        a.lastName.localeCompare(b.lastName, "pl", {
                          sensitivity: "base",
                        }),
                      )}
                      value={selectedSubordinateId}
                      valueExpr={(item) =>
                        item ? `${item.companyId},${item.employeeId}` : ""
                      }
                      displayExpr={(item: ManagerSubordinate) =>
                        item ? `${item.lastName} ${item.firstName}` : ""
                      }
                      width={200}
                      onValueChanged={(event) =>
                        setSelectedSubordinateId(event.value)
                      }
                      labelMode="static"
                      label={t("workPlan.employee")}
                      dropDownOptions={{
                        showTitle: false,
                      }}
                    />
                  </Item>
                  <Item location="after" locateInMenu="auto">
                    <Button
                      text={t("workPlan.edit")}
                      onClick={() => setShowWorkPlanEditor(true)}
                      disabled={!selectedDates || selectedDates.length === 0}
                    />
                  </Item>
                </Toolbar>
                {days.length > 0 && (
                  <div className="relative mx-auto max-w-5xl">
                    <Calendar
                      ref={calendarRef}
                      className="work-plan-for-employee"
                      value={selectedDates}
                      width="100%"
                      height={750}
                      minZoomLevel="decade"
                      firstDayOfWeek={1}
                      cellRender={cellRender}
                      disabledDates={disabledDates}
                      onOptionChanged={handleOptionChanged}
                      selectionMode="multiple"
                    />
                    {workPlanLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/60 z-10 rounded">
                        <LoadIndicator height={40} width={40} />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex flex-wrap justify-center gap-4 pt-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#a3e635]"></div>
                <div className="leading-none">{t("Holiday")}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#4d7c0f]"></div>
                <div className="leading-none">{t("Day off")}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#86efac]"></div>
                <div className="leading-none">{t("Leave")}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#facc15]"></div>
                <div className="leading-none">{t("workPlan.careleave")}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#f87171]"></div>
                <div className="leading-none">{t("workPlan.OtherLeave")}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </ScrollView>
  );
}
